/**
 * Upload cost-brake limits and musician-facing refusal copy.
 * Server enforcement comes in later steps; messages are shared now.
 */

export const MAX_FILE_BYTES = 2.5 * 1024 * 1024 * 1024; // 2.5 GB
export const MAX_UPLOADS_PER_SLOT = 3;
export const MAX_PORTAL_BYTES = 25 * 1024 * 1024 * 1024; // 25 GB
export const MAX_BUCKET_BYTES = 200 * 1024 * 1024 * 1024; // 200 GB hard stop

/** @typedef {'file_too_large' | 'slot_locked' | 'portal_full' | 'bucket_full'} UploadLimitReason */

export const UPLOAD_LIMIT_MESSAGES = {
  file_too_large:
    'This file is too large. Please choose a recording under 2.5 GB.',
  slot_locked:
    'This recording box is locked after 3 uploads. Contact Auddict if you need another take.',
  portal_full:
    'This portal has reached its upload limit. Contact Auddict if you need more space.',
  bucket_full:
    'Uploads are temporarily unavailable. Please try again later or contact Auddict.',
};

/**
 * @param {UploadLimitReason | string | null | undefined} reason
 * @returns {string | null}
 */
export function getUploadLimitMessage(reason) {
  if (!reason) return null;
  return UPLOAD_LIMIT_MESSAGES[reason] || null;
}

/**
 * Map a server/API error string to a known limit reason when possible.
 * @param {string | null | undefined} errorText
 * @returns {UploadLimitReason | null}
 */
export function matchUploadLimitReason(errorText) {
  if (!errorText) return null;
  const text = errorText.toLowerCase();
  if (text.includes('too large') || text.includes('file size') || text.includes('2.5')) {
    return 'file_too_large';
  }
  if (text.includes('locked') || text.includes('3 upload') || text.includes('upload limit for this')) {
    return 'slot_locked';
  }
  if (text.includes('portal') && (text.includes('limit') || text.includes('full') || text.includes('25'))) {
    return 'portal_full';
  }
  if (text.includes('temporarily unavailable') || text.includes('storage limit')) {
    return 'bucket_full';
  }
  return null;
}

/**
 * Whether the musician can try again by picking another file.
 * @param {UploadLimitReason | string | null | undefined} reason
 */
export function canRetryUploadLimit(reason) {
  return reason === 'file_too_large';
}

/**
 * @param {number} bytes
 * @returns {boolean}
 */
export function isFileOverSizeLimit(bytes) {
  return typeof bytes === 'number' && bytes > MAX_FILE_BYTES;
}

/**
 * Preview helper: ?previewUploadLimit=file|slot|portal|bucket
 * @param {string | null | undefined} value
 * @returns {UploadLimitReason | null}
 */
export function parsePreviewUploadLimit(value) {
  switch ((value || '').toLowerCase()) {
    case 'file':
      return 'file_too_large';
    case 'slot':
      return 'slot_locked';
    case 'portal':
      return 'portal_full';
    case 'bucket':
      return 'bucket_full';
    default:
      return null;
  }
}
